import { ChangeDetectorRef, Component, ElementRef, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { takeUntil } from 'rxjs/operators';
import { AppConfig } from 'src/app/configs/app.config';
import { AppRoute } from 'src/app/constants/app.route';
import { Authority } from 'src/app/constants/authority';
import { EventType } from 'src/app/constants/events-type';
import { DestroyNotifierComponent } from 'src/app/core/component/destroy/destroy-notifier.component';
import { AuthFactoryService } from 'src/app/core/services/auth/auth-factory.service';
import { EventManagerService } from 'src/app/core/services/event-manager/event-manager.service';
import { EventContent } from 'src/app/core/services/event-manager/models/event-content';
import { AuthenticatedUserService } from 'src/app/core/services/http/authenticated-user/authenticated-user.service';
import { School } from 'src/app/core/services/http/school/interfaces/school';
import { SchoolService } from 'src/app/core/services/http/school/school.service';
import { User } from 'src/app/core/services/http/user/interfaces/user';
import { MenuItem } from 'src/app/core/services/menu-builder/interfaces/menu-item';
import { MenuBuilderService } from 'src/app/core/services/menu-builder/menu-builder.service';
import { assignLocation, isFeatureEnabled } from 'src/app/core/utils/utils';
import { LiveNotificationService } from '../../core/services/notification/live-notification.service';
import { environment } from 'src/environments/environment';
import { AuthUserService } from 'src/app/core/services/auth/auth-user.service';
import { sha256 } from 'js-sha256';
import { LocalStorageService } from 'src/app/core/services/local-storage/local-storage.service';

declare const FS: any;
@Component({
  selector: 'app-menu',
  templateUrl: './menu.component.html'
})
export class MenuComponent extends DestroyNotifierComponent implements OnInit {
  user: User | null | undefined;
  appRoute = AppRoute;
  appConfig = AppConfig;
  authority = Authority;
  menuItems: MenuItem[] | undefined;
  subMenuItems: MenuItem[] | undefined;
  shouldShowSubMenu: boolean | undefined;
  school: School | undefined | null;
  shouldShowLoading: boolean | undefined;
  schools: School[] | undefined;
  unreadNotificationCount = 0;

  assingLocation = assignLocation;

  constructor(
    private schoolService: SchoolService,
    private eventManager: EventManagerService,
    private menuBuilderService: MenuBuilderService,
    private authFactoryService: AuthFactoryService,
    private router: Router,
    public el: ElementRef,
    public authenticatedUserService: AuthenticatedUserService,
    private liveNotificationService: LiveNotificationService,
    private cdRef: ChangeDetectorRef,
    public authUserService: AuthUserService,
    private localStorageService: LocalStorageService
  ) {
    super();
  }

  async ngOnInit(): Promise<void> {
    this.school = this.authFactoryService.service.getSchoolUserAuthority();

    if (!this.school) {
      return;
    }

    this.shouldShowSubMenu = this.menuBuilderService.getShouldShowSubMenuState();
    this.menuItems = await this.menuBuilderService.getMenuByAuthority(this.school.role);
    this.subMenuItems = await this.menuBuilderService.getSubMenuByAuthority(this.school.role);
    this.setIsActiveByCurrentRoute();
    await this.setUser();

    if (this.user && isFeatureEnabled('notifications')) {
      this.unreadNotificationCount = this.liveNotificationService.unreadCount;
      this.liveNotificationService
          .unreadNotificationCountChanged
          .subscribe((unreadCount: number) => {
            this.unreadNotificationCount = unreadCount;
            this.cdRef.detectChanges();
          });
    }

    this.eventManager.subscribe(EventType.IsAutoAssignUpdated,async () => {
      if(this.school){
        this.menuItems = await this.menuBuilderService.getMenuByAuthority(this.school.role);
        this.subMenuItems = await this.menuBuilderService.getSubMenuByAuthority(this.school.role);
      }
    });

    this.gainsightIdentifyEvent();
    if(!this.authenticatedUserService.isFullStoryDataSend) {
      this.trackFullStory();
    }
  }

  generateGainsightPXScript(aptrinsicKey: string) {
    const scriptTag = document.createElement('script');
    scriptTag.type = 'text/javascript';
    scriptTag.innerHTML = `(function(n,t,a,e,co){var i="aptrinsic";n[i]=n[i]||function(){
    (n[i].q=n[i].q||[]).push(arguments)},n[i].p=e;n[i].c=co;
    var r=t.createElement("script");r.async=!0,r.src=a+"?a="+e;
    var c=t.getElementsByTagName("script")[0];c.parentNode.insertBefore(r,c)
  })(window,document,"https://web-sdk.aptrinsic.com/api/aptrinsic.js",'${aptrinsicKey}');`;

    return scriptTag;
  }
  gainsightIdentifyEvent(): void {
    if (this.authenticatedUserService.isGainsightPxDataSend) {
      return;
    }

    if (!this.user || !this.user?.user || !this.school) {
      return;
    }

    const isStudent = this.user.role.code.toLowerCase() === 'student';

    // 1/32nd sampling for students
    if (isStudent) {
      const uuidPrefix = this.user.user.uuid?.substring(0, 2).toLowerCase();
      const allowedPrefixes = ['00', '01', '02', '03', '04', '05', '06', '07'];
      if (!allowedPrefixes.includes(uuidPrefix)) {
        return;
      }
    }

    const hashedUuid = sha256(this.user.user.uuid);

    const userData = {
      id: hashedUuid,
      email: isStudent ? null : this.user.user.email,
      firstName: isStudent ? 'Student' : this.user.user.firstName,
      lastName: isStudent ? 'Student' : this.user.user.lastName,
      roleFlex: this.user.role.code
    };

    const accountData = {
      id: this.school.uuid,
      name: this.school.name
    };

    const script = this.generateGainsightPXScript(environment.aptrinsicApiKey);

    document.body.appendChild(script);
    const aptrinsic = (window as any)?.aptrinsic || null;

    if (!aptrinsic) {
      return;
    }

    aptrinsic('identify', userData, accountData);
    this.authenticatedUserService.isGainsightPxDataSend = true;
  }

  updateActiveItemClass(selectedChildItem: MenuItem): void {
    if (!selectedChildItem.routerLink) return;
  
    this.router.navigateByUrl(selectedChildItem.routerLink).then(success => {
      if (!success) return;
  
      this.setActiveToInActive(this.menuItems);
      this.setActiveToInActive(this.subMenuItems);
  
      selectedChildItem.isActive = true;
  
      if (selectedChildItem.items && selectedChildItem.isParentLink) {
        selectedChildItem.isCollapsed = false;
      }
    });
  }

  onChooseSchool(school: School): void {
    const schoolUserAuthority = this.authFactoryService.service.getSchoolUserAuthority();

    if (schoolUserAuthority?.uuid === school.uuid) {
      return;
    }

    this.authFactoryService.service.setSchoolUserAuthority(school);
    const defaultRoute = AppConfig.DefaultRouteUserRoleMapping[school.role];

    if (defaultRoute) {
      this.navigate(defaultRoute);
    }
  }

  onLogOut(): void {
    this.authFactoryService.service.logOut();
  }

  onOpenSubMenu(): void {
    this.shouldShowSubMenu = true;
    this.menuBuilderService.setShouldShowSubMenuState(true);
  }

  onHideSubMenu(): void {
    this.shouldShowSubMenu = false;
    this.menuBuilderService.setShouldShowSubMenuState(false);
  }

  openMenu(): void {
    this.el.nativeElement.querySelector('#mobileMenu').classList.toggle('open');
    this.el.nativeElement.querySelector('#mainMenu').classList.toggle('show');
  }

  onImpersonate(): void {
    this.eventManager.broadcast(new EventContent(EventType.SimulateShow, null));
  }

  onOpenChange(isOpen: boolean): void {
    if (isOpen) {
      this.schools = undefined;
      this.loadSchools();
    }
  }

  navigateToNotifications() {
    this.router.navigateByUrl(AppRoute.Notifications);
  }

  private loadSchools(): void {
    this.shouldShowLoading = true;

    this.schoolService
      .getAll()
      .pipe(takeUntil(this.destroyNotify))
      .subscribe(schools => {
        this.schools = schools;
        this.shouldShowLoading = false;
      });
  }

  private setIsActiveByCurrentRoute(): void {
    const routerUrl = this.getAppRoute();
    let shouldSkipMenuItems: boolean | undefined;

    const deactivateMenuItems = (menuItems: Array<MenuItem>) => {
      menuItems.forEach((item: MenuItem) => {
        item.isActive = false;

        if (item.hasOwnProperty('items')) {
          item.items?.forEach((childItem: MenuItem) => childItem.isActive = false);
        }
      });
    };  

    if (this.menuItems) {
      deactivateMenuItems(this.menuItems);
    }

    if (this.menuItems  && this.subMenuItems) {
      deactivateMenuItems(this.menuItems);
      deactivateMenuItems(this.subMenuItems);
    }

    // Sub menu child items
    this.subMenuItems?.forEach(item => {
      const newActiveChildItem = item.items?.find(childItem => childItem.routerLink === routerUrl);
      if (newActiveChildItem && !newActiveChildItem.isActive) {
        if (item.isCollapsed) {
          item.isCollapsed = false;
        }

        this.shouldShowSubMenu = true;
        newActiveChildItem.isActive = true;
        shouldSkipMenuItems = true;
      }
    });

    // Skips matching menuitem with route if there is active menuitem in subMenuItems
    if (shouldSkipMenuItems) {
      return;
    }

    this.menuItems?.forEach(item => {
      const newActiveChildItem = item.items?.find(childItem => childItem.routerLink === routerUrl);

      if (newActiveChildItem && !newActiveChildItem.isActive) {
        if (item.isCollapsed) {
          item.isCollapsed = false;
        }
        newActiveChildItem.isActive = true;
      }

      // Deactivates prev selected menu item
      item.isActive = false;

      // Items without parent
      if (item.routerLink === routerUrl) {
        if (!item.isActive) {
          if (item.isCollapsed) {
            item.isCollapsed = false;
          }
          item.isActive = true;
        }
      }
    });
  }

  private getAppRoute(): string {
    let routerUrl = this.router.url.replace('/', '');
    const queryParamSegmentIndex = routerUrl.indexOf('?');

    if (queryParamSegmentIndex !== -1) {
      routerUrl = routerUrl.substring(0, routerUrl.indexOf('?'));
    }

    return routerUrl as AppRoute;
  }

  private async setUser(): Promise<void> {
    this.user = await this.authenticatedUserService.getAuthenticatedUser().toPromise();
  }

  private navigate(appRoute: AppRoute): void {
    this.assingLocation(appRoute);
  }

  private setActiveToInActive(menuItems?: MenuItem[]): void {
    menuItems?.forEach(item => {
      const activeChildItem = item.items?.find(childItem => childItem.isActive === true);

      if (item.isActive) {
        item.isActive = false;
      }

      if (activeChildItem?.isActive) {
        activeChildItem.isActive = false;
      }
    });
  }

  
  private trackFullStory(): void {

    const authenticatedUser = this.localStorageService.getItem('authenticatedUser');
    
    // Optimized null/empty check using optional chaining
    if (!authenticatedUser?.trim()) {
      console.warn('🔍 FullStory: No authenticated user data found in localStorage');
      return;
    }

    let parsedData;
    try {
      parsedData = JSON.parse(authenticatedUser);
    } catch (error) {
      console.error('🔍 FullStory: Failed to parse authenticated user data:', error);
      return;
    }

    const user = parsedData?.body?.user;
    const role = parsedData?.body?.role.code;

    if (!user) {
      console.warn('🔍 FullStory: User data not found in authenticated user data');
      return;
    }

    // Early return for students
    if (role === Authority.Student) {
      return;
    }
    
    const scriptTag = document.createElement('script');
    scriptTag.type = 'text/javascript';
    scriptTag.innerHTML =`window["_fs_host"]="fullstory.com";window["_fs_script"]="edge.fullstory.com/s/fs.js";window["_fs_org"]="o-235P2C-na1";window["_fs_namespace"]="FS";!function(m,n,e,t,l,o,g,y){var s,f,a=function(h){return!(h in m)||(m.console&&m.console.log&&m.console.log('FullStory namespace conflict. Please set window["_fs_namespace"].'),!1)}(e);function p(b){var h,d=[];function j(){h&&(d.forEach(function(b){var d;try{d=b[h[0]]&&b[h[0]](h[1])}catch(h){return void(b[3]&&b[3](h))}d&&d.then?d.then(b[2],b[3]):b[2]&&b[2](d)}),d.length=0)}function r(b){return function(d){h||(h=[b,d],j())}}return b(r(0),r(1)),{then:function(b,h){return p(function(r,i){d.push([b,h,r,i]),j()})}}}a&&(g=m[e]=function(){var b=function(b,d,j,r){function i(i,c){h(b,d,j,i,c,r)}r=r||2;var c,u=/Async$/;return u.test(b)?(b=b.replace(u,""),"function"==typeof Promise?new Promise(i):p(i)):h(b,d,j,c,c,r)};function h(h,d,j,r,i,c){return b._api?b._api(h,d,j,r,i,c):(b.q&&b.q.push([h,d,j,r,i,c]),null)}return b.q=[],b}(),y=function(b){function h(h){"function"==typeof h[4]&&h[4](new Error(b))}var d=g.q;if(d){for(var j=0;j<d.length;j++)h(d[j]);d.length=0,d.push=h}},function(){(o=n.createElement(t)).async=!0,o.crossOrigin="anonymous",o.src="https://"+l,o.onerror=function(){y("Error loading "+l)};var b=n.getElementsByTagName(t)[0];b&&b.parentNode?b.parentNode.insertBefore(o,b):n.head.appendChild(o)}(),function(){function b(){}function h(b,h,d){g(b,h,d,1)}function d(b,d,j){h("setProperties",{type:b,properties:d},j)}function j(b,h){d("user",b,h)}function r(b,h,d){j({uid:b},d),h&&j(h,d)}g.identify=r,g.setUserVars=j,g.identifyAccount=b,g.clearUserCookie=b,g.setVars=d,g.event=function(b,d,j){h("trackEvent",{name:b,properties:d},j)},g.anonymize=function(){r(!1)},g.shutdown=function(){h("shutdown")},g.restart=function(){h("restart")},g.log=function(b,d){h("log",{level:b,msg:d})},g.consent=function(b){h("setIdentity",{consent:!arguments.length||b})}}(),s="fetch",f="XMLHttpRequest",g._w={},g._w[f]=m[f],g._w[s]=m[s],m[s]&&(m[s]=function(){return g._w[s].apply(this,arguments)}),g._v="2.0.0")} (window,document,window._fs_namespace,"script",window._fs_script);`

    document.head.appendChild(scriptTag);

    // Check FS availability before creating identity data
    if (typeof FS === 'undefined') {
      console.error('🔍 FullStory: FS object not available');
      return;
    }

    // Optimized identity data creation with null safety
    const identityData = {
      uid: user.uuid,
      properties: {
        displayName: `${user.firstName || ''} ${user.lastName || ''}`.trim(),
        email: user.email || '',
        roleFlex: role
      }
    };

    try {
      FS('setIdentity', identityData);
      this.authenticatedUserService.isFullStoryDataSend = true;
    } catch (error) {
      console.error('🔍 FullStory: Failed to set identity:', error);
    }
  }
}
